import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import themes, {
  ThemeName,
  ThemeTemplateId,
  ThemeDefinition,
  THEME_TEMPLATES,
  THEME_TEMPLATE_LIST,
  executiveIndigo
} from '../styles/themes';
import storage, { STORAGE_KEYS } from '../utils/storage';

export type DisplayMode = 'light' | 'dark' | 'system';
export type ThemeMode = DisplayMode | ThemeName;

export interface ThemeContextType {
  template: ThemeTemplateId;
  setTemplate: (t: ThemeTemplateId) => Promise<void>;
  mode: ThemeMode;
  setMode: (m: ThemeMode) => Promise<void>;
  toggleTheme: () => Promise<void>;
  themeName: ThemeName;
  currentTheme: ThemeDefinition;
  availableTemplates: ThemeDefinition[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_MODE_KEY = STORAGE_KEYS.THEME;
const TEMPLATE_KEY = STORAGE_KEYS.TEMPLATE;

function resolveSystemPref(): 'dark' | 'light' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return dark ? 'dark' : 'light';
  }
  return 'light';
}

function applyThemeToRoot(templateId: ThemeTemplateId, activeMode: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const definition = THEME_TEMPLATES[templateId] || executiveIndigo;
  const colors = definition[activeMode];

  // Primary brand colors
  root.style.setProperty('--brand-primary', colors.primary);
  root.style.setProperty('--brand-primary-hover', colors.primaryHover);
  root.style.setProperty('--brand-50', colors.badgeBg);
  root.style.setProperty('--brand-100', colors.badgeBorder);
  root.style.setProperty('--brand-200', colors.badgeBorder);
  root.style.setProperty('--color-sec', colors.secondary);
  root.style.setProperty('--color-acc', colors.accent);

  // Surfaces & Backgrounds
  root.style.setProperty('--bg-app', colors.bg);
  root.style.setProperty('--bg-surface', colors.surface);
  root.style.setProperty('--color-card', colors.card || colors.surface);
  root.style.setProperty('--color-surface', colors.surface);
  root.style.setProperty('--color-bg', colors.bg);

  // Typography
  root.style.setProperty('--text-main', colors.text);
  root.style.setProperty('--text-muted', colors.muted);
  root.style.setProperty('--color-muted', colors.muted);

  // Borders & Glow
  root.style.setProperty('--border-main', colors.border);
  root.style.setProperty('--border-subtle', colors.borderSubtle);
  root.style.setProperty('--color-border', colors.border);
  root.style.setProperty('--color-border-light', colors.borderSubtle);
  root.style.setProperty('--glass-border', colors.glassBorder);
  root.style.setProperty('--theme-glow', colors.glow);

  // Legacy mappings for backward compatibility
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-primary-inv', colors.inverse || (activeMode === 'dark' ? '#090d16' : '#ffffff'));

  // Expose attributes for Tailwind dark: and data-theme selectors
  root.setAttribute('data-theme', templateId);
  root.setAttribute('data-mode', activeMode);

  if (activeMode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [template, setTemplateState] = useState<ThemeTemplateId>('indigo');
  const [mode, setModeState] = useState<ThemeMode>('light');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [savedMode, savedTemplate] = await Promise.all([
        storage.getItem(THEME_MODE_KEY),
        storage.getItem(TEMPLATE_KEY)
      ]);

      if (!mounted) return;

      // Restore template
      if (
        savedTemplate === 'indigo' ||
        savedTemplate === 'emerald' ||
        savedTemplate === 'sapphire' ||
        savedTemplate === 'amethyst' ||
        savedTemplate === 'obsidian'
      ) {
        setTemplateState(savedTemplate);
      }

      // Restore mode
      if (savedMode === 'dark' || savedMode === 'light' || savedMode === 'system') {
        setModeState(savedMode);
      } else if (savedMode === 'brand') {
        setModeState('light');
      } else {
        setModeState('system');
      }

      setLoaded(true);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const systemPref = useMemo(() => resolveSystemPref(), [loaded]);

  const activeMode: 'light' | 'dark' = useMemo(() => {
    if (mode === 'system') return systemPref;
    if (mode === 'dark') return 'dark';
    return 'light';
  }, [mode, systemPref]);

  const currentTheme = useMemo(() => {
    return THEME_TEMPLATES[template] || executiveIndigo;
  }, [template]);

  const themeName: ThemeName = useMemo(() => {
    return activeMode === 'dark' ? 'dark' : 'light';
  }, [activeMode]);

  useEffect(() => {
    applyThemeToRoot(template, activeMode);
  }, [template, activeMode]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (mode === 'system') {
        applyThemeToRoot(template, resolveSystemPref());
      }
    };
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler as any);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler as any);
    };
  }, [mode, template]);

  const setTemplate = async (t: ThemeTemplateId) => {
    setTemplateState(t);
    try {
      await storage.setItem(TEMPLATE_KEY, t);
    } catch (e) {}
  };

  const setMode = async (m: ThemeMode) => {
    setModeState(m);
    try {
      await storage.setItem(THEME_MODE_KEY, m);
    } catch (e) {}
  };

  const toggleTheme = async () => {
    const next: ThemeMode = activeMode === 'light' ? 'dark' : 'light';
    await setMode(next);
  };

  if (!loaded) return null;

  return (
    <ThemeContext.Provider
      value={{
        template,
        setTemplate,
        mode,
        setMode,
        toggleTheme,
        themeName,
        currentTheme,
        availableTemplates: THEME_TEMPLATE_LIST
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export default ThemeProvider;
